import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Checkbox, Form, Input, List, Modal, Select, Space, Tag, Typography, message } from 'antd';
import { apiClient, apiErrorMessage } from '../utils';
import { Filter, FilterGroup } from '../models';
import { RecordsPreview } from './records-preview';
import { ConditionsViewer } from './conditions-viewer';
import { ConditionsBuilder } from './conditions-builder';
import { CheckSquareOutlined, EyeOutlined, TableOutlined, PlusOutlined } from '@ant-design/icons';

interface FilterSelectorProps {
  onChange?: (value: FilterGroup | undefined) => void;
  dataSetId?: string;
  value?: FilterGroup;
  size?: 'normal' | 'small';
}

export function FilterSelector(props: FilterSelectorProps) {
  const [filters, setFilters] = useState<Filter[]>();
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState<FilterGroup>();
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [conditionsModalVisible, setConditionsModalVisible] = useState(false);
  const [selectionModalVisible, setSelectionModalVisible] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([]);
  const [selectedFilterPreview, setSelectedFilterPreview] = useState<Filter | null>(null);
  const [tempSelectedFilterIds, setTempSelectedFilterIds] = useState<string[]>([]);
  const [newFilterModalVisible, setNewFilterModalVisible] = useState(false);
  const [newFilterSubmitting, setNewFilterSubmitting] = useState(false);
  const [newFilterForm] = Form.useForm();

  const valueRef = useRef<FilterGroup>({ logic: 'AND', filterIds: [] });

  useEffect(() => {
    const nextValue = props.value ?? { logic: 'AND', filterIds: [] };
    setValue(nextValue);
    valueRef.current = nextValue;
  }, [props.value]);

  useEffect(() => {
    if (!value?.filterIds?.length || !filters) {
      setSelectedFilters([]);
      return;
    }
    const selected = filters.filter(f => value.filterIds.includes(f.id));
    selected.sort((a, b) => {
      const indexA = value.filterIds.indexOf(a.id);
      const indexB = value.filterIds.indexOf(b.id);
      return indexA - indexB;
    });
    setSelectedFilters(selected);
  }, [value, filters]);

  const loadFilters = useCallback(() => {
    setLoading(true);

    const params = [];
    if (props.dataSetId) {
      params.push(`dataSetId=${props.dataSetId}`);
    }
    let url = 'filters';
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    apiClient
      .get(url)
      .then((response: any) => {
        setFilters(response.data?.filters as Filter[]);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [props.dataSetId]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  const handleLogicChange = (value: 'AND' | 'OR') => {
    valueRef.current.logic = value;
    setValue({ ...valueRef.current });
    if (valueRef.current.filterIds.length === 0) {
      props.onChange?.(undefined);
    } else {
      props.onChange?.(valueRef.current);
    }
  };

  const handleFilterChange = (value: string[]) => {
    valueRef.current.filterIds = value;
    setValue({ ...valueRef.current });
    if (valueRef.current.filterIds.length === 0) {
      props.onChange?.(undefined);
    } else {
      props.onChange?.(valueRef.current);
    }
  };

  const handlePreviewClick = () => {
    setPreviewModalVisible(true);
  };

  const handlePreviewClose = () => {
    setPreviewModalVisible(false);
    setSelectedFilterPreview(null);
  };

  const handleConditionsClick = () => {
    setConditionsModalVisible(true);
  };

  const handleConditionsClose = () => {
    setConditionsModalVisible(false);
  };

  const handleFilterPreviewClick = (filter: Filter) => {
    setSelectedFilterPreview(filter);
    setPreviewModalVisible(true);
  };

  const handleSelectionModalOpen = () => {
    setTempSelectedFilterIds(value?.filterIds ?? []);
    setSelectionModalVisible(true);
  };

  const handleSelectionModalClose = () => {
    setSelectionModalVisible(false);
  };

  const handleSelectionModalOk = () => {
    handleFilterChange(tempSelectedFilterIds);
    setSelectionModalVisible(false);
  };

  const handleFilterCheckboxChange = (filterId: string, checked: boolean) => {
    if (checked) {
      setTempSelectedFilterIds([...tempSelectedFilterIds, filterId]);
    } else {
      setTempSelectedFilterIds(tempSelectedFilterIds.filter(id => id !== filterId));
    }
  };

  const handleNewFilterModalOpen = () => {
    newFilterForm.resetFields();
    setNewFilterModalVisible(true);
  };

  const handleNewFilterModalClose = () => {
    setNewFilterModalVisible(false);
    newFilterForm.resetFields();
  };

  const handleNewFilterSubmit = async (values: any) => {
    setNewFilterSubmitting(true);
    try {
      const body = {
        name: values.name,
        description: values.description || '',
        dataSetId: props.dataSetId,
        conditions: values.conditions,
      };
      const response = await apiClient.post('filters', body);
      const newFilter = response.data;
      setFilters([...(filters || []), newFilter]);
      setTempSelectedFilterIds([...tempSelectedFilterIds, newFilter.id]);
      message.success('Filtre başarıyla oluşturuldu.');
      handleNewFilterModalClose();
    } catch (reason: any) {
      apiErrorMessage(reason);
    } finally {
      setNewFilterSubmitting(false);
    }
  };

  const LogicTag = ({ logic }: { logic: 'AND' | 'OR' }) => (
    <Tag
      style={{
        borderRadius: '12px',
        backgroundColor: logic === 'AND' ? '#e6f7ff' : '#fff7e6',
        border: `1px solid ${logic === 'AND' ? '#91d5ff' : '#ffd591'}`,
        color: logic === 'AND' ? '#1890ff' : '#fa8c16',
        fontWeight: 600,
        margin: '0 8px',
      }}
    >
      {logic === 'AND' ? 'VE' : 'VEYA'}
    </Tag>
  );

  const selectSize = props.size === 'small' ? 'small' : 'middle';

  return (
    <>
      <div style={{ display: 'flex', gap: selectSize === 'small' ? '6px' : '8px', width: '100%' }}>
        <Select
          value={value?.logic}
          onChange={handleLogicChange}
          style={{ width: selectSize === 'small' ? 75 : 80, flexShrink: 0 }}
          size={selectSize}
          disabled={!props.dataSetId}
          options={[
            { label: 'VE', value: 'AND' },
            { label: 'VEYA', value: 'OR' },
          ]}
        />
        <Select
          mode="multiple"
          value={value?.filterIds}
          onChange={handleFilterChange}
          style={{ flex: 1, minWidth: 0 }}
          size={selectSize}
          loading={loading}
          placeholder="Filtre seçin"
          disabled={!props.dataSetId}
          options={filters?.map(f => ({
            label: f.name,
            value: f.id,
          }))}
        />
        <Button
          icon={<CheckSquareOutlined />}
          onClick={handleSelectionModalOpen}
          disabled={!props.dataSetId}
          size={selectSize}
          style={{ flexShrink: 0 }}
          title="Detaylı seçim"
        />
        <Button
          icon={<EyeOutlined />}
          onClick={handleConditionsClick}
          disabled={!value?.filterIds?.length}
          size={selectSize}
          style={{ flexShrink: 0 }}
          title="Koşulları görüntüle"
        />
        <Button
          icon={<TableOutlined />}
          onClick={handlePreviewClick}
          disabled={!value?.filterIds?.length}
          size={selectSize}
          style={{ flexShrink: 0 }}
          title="Önizleme"
        />
      </div>

      <Modal
        title="Filtre Koşulları"
        visible={conditionsModalVisible}
        onCancel={handleConditionsClose}
        footer={
          <Button
            type="primary"
            icon={<TableOutlined />}
            onClick={() => {
              handleConditionsClose();
              handlePreviewClick();
            }}
          >
            Tüm filtreleri önizle
          </Button>
        }
        width="80%"
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {selectedFilters.map((filter, index) => (
            <React.Fragment key={filter.id}>
              {index > 0 && (
                <div style={{ textAlign: 'center', margin: '16px 0' }}>
                  <LogicTag logic={value?.logic ?? 'AND'} />
                </div>
              )}
              <Card
                size="small"
                title={
                  <Space>
                    <Typography.Text strong>{filter.name}</Typography.Text>
                    {filter.description && (
                      <Typography.Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                        ({filter.description})
                      </Typography.Text>
                    )}
                  </Space>
                }
                extra={
                  <Button size="small" icon={<EyeOutlined />} onClick={() => handleFilterPreviewClick(filter)}>
                    Önizle
                  </Button>
                }
              >
                <ConditionsViewer conditions={filter.conditionsDisplay} layout="vertical" />
              </Card>
            </React.Fragment>
          ))}
        </Space>
      </Modal>

      <Modal
        title="Filtre seçimi"
        visible={selectionModalVisible}
        onCancel={handleSelectionModalClose}
        onOk={handleSelectionModalOk}
        okText="Uygula"
        cancelText="İptal"
        width="80%"
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={handleSelectionModalClose}>
            İptal
          </Button>,
          <Button
            key="preview"
            icon={<TableOutlined />}
            onClick={() => {
              const filterGroup: FilterGroup = {
                logic: value?.logic ?? 'AND',
                filterIds: tempSelectedFilterIds,
              };
              valueRef.current = filterGroup;
              setPreviewModalVisible(true);
            }}
            disabled={tempSelectedFilterIds.length === 0}
          >
            Seçili filtreleri önizle
          </Button>,
          <Button key="submit" type="primary" onClick={handleSelectionModalOk}>
            Uygula
          </Button>,
        ]}
      >
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography.Text type="secondary">
            Kullanmak istediğiniz filtreleri seçin. Seçili filtreler{' '}
            <Tag
              style={{
                borderRadius: '12px',
                backgroundColor: value?.logic === 'AND' ? '#e6f7ff' : '#fff7e6',
                border: `1px solid ${value?.logic === 'AND' ? '#91d5ff' : '#ffd591'}`,
                color: value?.logic === 'AND' ? '#1890ff' : '#fa8c16',
                fontWeight: 600,
              }}
            >
              {value?.logic === 'AND' ? 'VE' : 'VEYA'}
            </Tag>{' '}
            mantığı ile birleştirilecektir.
          </Typography.Text>
          <Button
            type='primary'
            icon={<PlusOutlined />}
            onClick={handleNewFilterModalOpen}
            disabled={!props.dataSetId}
          >
            Yeni filtre
          </Button>
        </div>
        <List
          dataSource={filters}
          loading={loading}
          renderItem={(filter) => (
            <List.Item key={filter.id} style={{ padding: '12px 0' }}>
              <div style={{ display: 'flex', width: '100%', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, paddingTop: '8px' }}>
                  <Checkbox
                    checked={tempSelectedFilterIds.includes(filter.id)}
                    onChange={(e) => handleFilterCheckboxChange(filter.id, e.target.checked)}
                  />
                </div>
                <Card
                  size="small"
                  style={{ width: '100%', flex: 1 }}
                  title={
                    <Space>
                      <Typography.Text strong>{filter.name}</Typography.Text>
                      {filter.description && (
                        <Typography.Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                          ({filter.description})
                        </Typography.Text>
                      )}
                    </Space>
                  }
                  extra={
                    <Button
                      size="small"
                      icon={<TableOutlined />}
                      onClick={() => {
                        handleFilterPreviewClick(filter);
                      }}
                    >
                      Önizle
                    </Button>
                  }
                >
                  <ConditionsViewer conditions={filter.conditionsDisplay} layout="vertical" />
                </Card>
              </div>
            </List.Item>
          )}
        />
      </Modal>

      <Modal
        title={selectedFilterPreview ? `Filtre önizleme: ${selectedFilterPreview.name}` : 'Filtre önizleme'}
        visible={previewModalVisible}
        onCancel={handlePreviewClose}
        footer={null}
        width="80%"
        destroyOnClose
      >
        {selectedFilterPreview ? (
          <RecordsPreview dataSetId={props.dataSetId} filter={selectedFilterPreview} />
        ) : (
          <RecordsPreview dataSetId={props.dataSetId} filterGroup={valueRef.current} />
        )}
      </Modal>

      <Modal
        title="Yeni filtre oluştur"
        visible={newFilterModalVisible}
        onCancel={handleNewFilterModalClose}
        footer={[
          <Button key="cancel" onClick={handleNewFilterModalClose}>
            İptal
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={newFilterSubmitting}
            onClick={() => newFilterForm.submit()}
          >
            Oluştur
          </Button>,
        ]}
        width="80%"
        destroyOnClose
      >
        <Form
          layout="vertical"
          form={newFilterForm}
          onFinish={handleNewFilterSubmit}
        >
          <Form.Item
            label="İsim"
            name="name"
            rules={[
              { required: true, message: 'Zorunlu alan' },
              {
                pattern: /^[a-zA-Z0-9ıİğĞüÜşŞöÖçÇ][a-zA-Z0-9_\-ıİğĞüÜşŞöÖçÇ\s]*$/,
                message: 'Sadece harf, rakam, boşluk, - ve _ karakteri kullanılabilir',
              },
              { max: 100, message: 'En fazla 100 karakter' },
            ]}
            wrapperCol={{ span: 12 }}
          >
            <Input placeholder="Filtre adı giriniz" />
          </Form.Item>

          <Form.Item
            label="Açıklama"
            name="description"
            rules={[{ max: 400, message: 'En fazla 400 karakter' }]}
            wrapperCol={{ span: 12 }}
          >
            <Input.TextArea placeholder="Açıklama giriniz (isteğe bağlı)" />
          </Form.Item>

          <Form.Item label="Koşullar" name="conditions" rules={[{ required: true, message: 'Zorunlu alan' }]}>
            <ConditionsBuilder
              dataSetId={props.dataSetId}
              pendingSelectMessage="Koşulları belirlemek için bir veri seti seçilmelidir"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
